import type { ApiErrorBody, NonPositivePayoutBalanceError } from '../types';

/**
 * Narrow an unknown thrown value to an axios-shaped API error.
 *
 * Every hook here receives `unknown` from React Query rather than `any`, so this is
 * the single place that knows the wire shape: axios nests the server body under
 * `response.data`, and the API's error body is `{ success, statusCode, error,
 * message }` (docs "Conventions").
 */
export interface ApiError {
  response?: {
    status?: number;
    data?: ApiErrorBody & NonPositivePayoutBalanceError;
  };
  message?: string;
}

export const asApiError = (error: unknown): ApiError =>
  (typeof error === 'object' && error !== null ? error : {}) as ApiError;

/** The server body of an error, when there is one. */
export const apiErrorBody = (
  error: unknown
): (ApiErrorBody & NonPositivePayoutBalanceError) | undefined =>
  asApiError(error).response?.data;

export const apiErrorStatus = (error: unknown): number | undefined =>
  asApiError(error).response?.status;

/**
 * Pull a useful message out of an API error.
 *
 * `message` is the field worth showing — a duplicate email or a taken code prefix
 * both arrive as a 400 with a specific message, and surfacing it beats a generic
 * failure.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const wrapped = asApiError(error);
  return (
    wrapped.response?.data?.message ||
    wrapped.response?.data?.error ||
    wrapped.message ||
    fallback
  );
};
